import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as S from "./TransducerGoogleChirp2.style";
import ZaiaImage from "../../../../zaiaComponents/zaiaImage";
import ZaiaSelect from "../../../../zaiaComponents/zaiaSelect";
import * as P from "../../../../../utils/pipes";
import TranducerMessagesList from "../tranducerMessagesList";
import useInfoToken from "../../../../../hooks/useInfoToken";

const record = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaFigures/record.svg`;
const talkingHead = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaFigures/talking-head.svg`;
const talkingHeads = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaFigures/talking-heads.svg`;

// La URL base del socket desde tu HTML
const BASE_WS_URL = "wss://transcriptor-server-54644131242.us-central1.run.app/transcribe";

function TransducerGoogleChirp2({
  transducerTypes,
  transducerType,
  setTransducerType,
  recordingActived,
  setRecordingActived,
  transducerState,
  setTransducerState,
  setManageTransductionActiveConfirmation,
  messages,
  setMessages,
  transcription,
  setTranscription
}) {
  const { t, i18n: { language } } = useTranslation("global");

  const [interimText, setInterimText] = useState(""); // Para el texto en gris (borrador)
  const { token } = useInfoToken();

  const mediaStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const socketRef = useRef(null);
  const isUserStoppedRef = useRef(true); // Ref para controlar el estado de cierre

  useEffect(() => {
    setTransducerType(1);
    setTransducerState(null);
    stopRecording()
    // Ping inicial para despertar el servidor (Warm-up)
    const warmUp = async () => {
      try {
        const httpUrl = BASE_WS_URL.replace("wss://", "https://").replace("/transcribe", "/");
        await fetch(httpUrl);
        await fetch(httpUrl);
      } catch (e) {
        console.warn("Error despertando servidor", e);
      }
    };
    warmUp();

    return () => stopRecording();
  }, []);

  // --- LÓGICA DE WEBSOCKET ---

  const connectSocket = (lang) => {
    if (isUserStoppedRef.current) return;

    const wsUrl = `${BASE_WS_URL}?lang=${lang}`;
    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;

    socket.onopen = () => {
      setTransducerState('listening');
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.error) {
        console.error("Server Error:", data.error);
        return;
      }

      if (data.is_final) {
        // Texto confirmado: lo agregamos a la transcripción principal
        setTranscription((prev) => prev + data.text + " ");
        setInterimText(""); // Limpiamos el borrador
      } else {
        // Texto borrador: actualizamos el estado temporal
        setInterimText(data.text);
      }
    };

    socket.onclose = () => {
      console.log("Socket cerrado");
      // Si el usuario no lo detuvo manualmente, intentamos reconectar
      if (!isUserStoppedRef.current) {
        console.log("Reconectando...");
        setTimeout(() => connectSocket(lang), 500);
      }
    };

    socket.onerror = (err) => {
      console.error("WebSocket Error:", err);
      setTransducerState('error');
    };
  };

  // --- CONTROL DE GRABACIÓN ---

  const startRecordingNoteTaking = async () => {
    try {
      isUserStoppedRef.current = false;
      const simpleLang = P.languageSplited(language);

      let lang = "en-US";
      
      if(simpleLang === "es") {
        lang = "es-US";
      } else if(simpleLang === "fr") {
        lang = "fr-FR"
      } else if(simpleLang === "pt") { 
        lang = "pt-BR"
      }

      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      // Iniciar el socket primero
      setTransducerState('connecting');
      connectSocket(lang);

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0 && socketRef.current?.readyState === WebSocket.OPEN) {
          socketRef.current.send(event.data);
        }
      };

      // Enviamos pequeños trozos de audio cada 250ms (Flujo continuo)
      recorder.start(250);

    } catch (err) {
      console.error("Error al iniciar grabación:", err);
      // alert("No se pudo acceder al micrófono.");
      setTransducerState('error');
    }
  };

  const stopRecording = () => {
    isUserStoppedRef.current = true;
    
    // Si hay texto temporal al cerrar, lo pasamos a la transcripción final
    if (interimText) {
      setTranscription(prev => prev + interimText + " ");
      setInterimText("");
    }

    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    if (socketRef.current) {
      socketRef.current.close();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }
    
    
    // setStatus("⏹️ Sesión finalizada");
    setTransducerState('paused');
  };

  const finalizeSession = () => {
    setManageTransductionActiveConfirmation(true);
    setTransducerState('paused');
    stopRecording();
  };

  return (
    <S.TransducerGoogleChirp2 modelsSelection={P.languageSplited(language) === "en"}>
      <div className="transducer-content">
        {recordingActived && (
          <div className="messages-wrapper">
            <TranducerMessagesList
              transducerState={transducerState}
              transducerType={transducerType}
              // Combinamos la transcripción final con el texto en gris que viene del socket
              transcription={transcription + (interimText ? `...${interimText}` : "")}
              messages={messages}
            />
            {/* Opcional: Mostrar el status debajo de los mensajes */}
            {/* <div style={{ fontSize: '12px', textAlign: 'center', marginTop: '5px', color: '#666' }}>
                {status}
            </div> */}
          </div>
        )}
        
        {!recordingActived && (
          <TransductorChooseType
            transducerTypes={transducerTypes}
            transducerType={transducerType}
            setTransducerType={setTransducerType}
          />
        )}
      </div>

      <div className="space-start-recording">
        {transducerState === null && (
          <button
            className="recording-button"
            onClick={() => {
              if (transducerType) {
                setRecordingActived(true);
                startRecordingNoteTaking();
              }
            }}
          >
            <div className="recording-button__text">{t("transductor.startSession")}</div>
            {transducerType ? (
              <div className="recording-button__circle"></div>
            ) : (
              <i className="recording-button__icon icon zaia-i-lock-chat"></i>
            )}
          </button>
        )}

        {transducerState !== null && (
          <div className="active-recording-buttons">
            {transducerState === 'listening' && (
              <button
                className="recording-button recording-button--pause"
                onClick={stopRecording}
              >
                <i className="recording-button__icon recording-button__icon--pause icon zaia-i-paused"></i>
              </button>
            )}
            
            {(transducerState === 'paused' || transducerState === 'error') && (
              <button
                className="recording-button recording-button--continue"
                onClick={() => {
                  setRecordingActived(true);
                  startRecordingNoteTaking()
                }}
              >
                <div className="recording-button__text">{t("globally.continue")}</div>
                <div className="recording-button__circle"></div>
              </button>
            )}

            <button
              className={`recording-button recording-button--finish ${(transcription || messages.length > 0) && "recording-button--finish--active"}`}
              onClick={() => (transcription || messages.length > 0) && finalizeSession()}
            >
              {t("transductor.finalizeSession")}
            </button>
          </div>
        )}
      </div>
    </S.TransducerGoogleChirp2>
  );
}

// Componente auxiliar de selección
function TransductorChooseType({ transducerType, setTransducerType, transducerTypes }) {
  const { t } = useTranslation("global"); 
  return (
    <div className="transducer-choose-type">
      <div className="message-space">
        <div className="message-space__title">{t("transductor.advancedNoteTaking")}</div>
        {transducerType === 1 && <img src={talkingHead} alt="note" className="message-space__img--note-taking" />}
        {transducerType === 2 && <img src={talkingHeads} alt="consult" className="message-space__img--medical-consultation" />}
        {!transducerType && <img src={record} alt="record" className="message-space__img--start" />}
        <div className="message-space__text">
          {!transducerType ? t("transductor.selectAMode") : transducerTypes.find(type => type.id === transducerType)?.description}
        </div>
      </div>
    </div>
  );
}

export default TransducerGoogleChirp2;